<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useConvexQuery, useConvexClient } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { useConvexAuth } from '../composables/useConvexAuth';
import { useUserSettings } from '../composables/useUserSettings';
import type { Id } from '../../convex/_generated/dataModel';
import { SUPPORTED_LOCALES, LOCALE_NAMES, type SupportedLocale } from '../plugins/i18n';
import SkeuoDropdown from './SkeuoDropdown.vue';

interface WorkshopData {
  id: Id<'workshops'>;
  name: string;
  slug: string;
  role: 'owner' | 'member' | 'reader' | null;
  lastActivityAt: number;
  votesLocked?: boolean;
  commentsEnabled?: boolean;
}

const props = defineProps<{
  modelValue: boolean;
  workshop?: WorkshopData | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  showWelcome: [];
}>();

const router = useRouter();
const client = useConvexClient();
const { signOut } = useConvexAuth();
const { data: settings, isPending: settingsLoading, update } = useUserSettings();

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
});

const showDeleteAccountConfirm = ref(false);
const showDeleteWorkshopConfirm = ref(false);
const workshopNameConfirm = ref('');
const accountEmailConfirm = ref('');
const isDeleting = ref(false);
const deleteError = ref('');

// Get current user for account deletion confirmation
const { data: currentUser } = useConvexQuery(api.auth.currentUser, () => ({}));

const isLoading = computed(() => settingsLoading.value);

const isWorkshopOwner = computed(() => props.workshop?.role === 'owner');

const canDeleteWorkshop = computed(() => {
  return workshopNameConfirm.value === props.workshop?.name;
});

const canDeleteAccount = computed(() => {
  return accountEmailConfirm.value === currentUser.value?.email;
});

const prefillEnabled = computed({
  get: () => settings.value?.prefillNameOnIdeas ?? true,
  set: async (value: boolean) => {
    await update({ prefillNameOnIdeas: value });
  },
});

// Detect browser language for display
const browserLocaleName = computed(() => {
  const browserLang = navigator.language?.toLowerCase().split('-')[0];
  const detected = browserLang === 'de' ? 'de' : 'en';
  return LOCALE_NAMES[detected as SupportedLocale];
});

// Locale selection (null = use browser default)
const selectedLocale = computed({
  get: () => settings.value?.locale ?? '',
  set: async (value: string) => {
    const newLocale = value || null;
    await update({ locale: newLocale as SupportedLocale | null });
  },
});

const handleLogout = async () => {
  await signOut();
  isOpen.value = false;
  window.location.href = '/';
};

const handleDeleteAccount = async () => {
  if (!canDeleteAccount.value) return;

  isDeleting.value = true;
  deleteError.value = '';

  try {
    await client.mutation(api.users.deleteAccount, {});
    await signOut();
    isOpen.value = false;
    showDeleteAccountConfirm.value = false;
    accountEmailConfirm.value = '';
    window.location.href = '/';
  } catch (e: any) {
    deleteError.value = e.message || 'Failed to delete account';
    isDeleting.value = false;
  }
};

const handleDeleteWorkshop = async () => {
  if (!props.workshop || !canDeleteWorkshop.value) return;

  isDeleting.value = true;
  deleteError.value = '';

  try {
    await client.mutation(api.workshops.remove, {
      workshopId: props.workshop.id,
    });
    isOpen.value = false;
    showDeleteWorkshopConfirm.value = false;
    workshopNameConfirm.value = '';
    // Navigate to workshops list after deletion
    router.push('/workshops');
  } catch (e: any) {
    deleteError.value = e.message || 'Failed to delete workshop';
    isDeleting.value = false;
  }
};

const close = () => {
  isOpen.value = false;
};

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// Votes locked state
const votesLocked = computed({
  get: () => props.workshop?.votesLocked ?? false,
  set: async (value: boolean) => {
    if (!props.workshop || !isWorkshopOwner.value) return;
    try {
      await client.mutation(api.workshops.setVotesLocked, {
        workshopId: props.workshop.id,
        locked: value,
      });
    } catch (e: any) {
      console.error('Failed to update votes locked:', e);
    }
  },
});

const toggleVotesLocked = async () => {
  if (!props.workshop || !isWorkshopOwner.value) return;
  const newValue = !votesLocked.value;
  try {
    await client.mutation(api.workshops.setVotesLocked, {
      workshopId: props.workshop.id,
      locked: newValue,
    });
  } catch (e: any) {
    console.error('Failed to update votes locked:', e);
  }
};

// Comments enabled state
const commentsEnabled = computed({
  get: () => props.workshop?.commentsEnabled ?? true,
  set: async (value: boolean) => {
    if (!props.workshop || !isWorkshopOwner.value) return;
    try {
      await client.mutation(api.workshops.setCommentsEnabled, {
        workshopId: props.workshop.id,
        enabled: value,
      });
    } catch (e: any) {
      console.error('Failed to update comments enabled:', e);
    }
  },
});

const toggleCommentsEnabled = async () => {
  if (!props.workshop || !isWorkshopOwner.value) return;
  const newValue = !commentsEnabled.value;
  try {
    await client.mutation(api.workshops.setCommentsEnabled, {
      workshopId: props.workshop.id,
      enabled: newValue,
    });
  } catch (e: any) {
    console.error('Failed to update comments enabled:', e);
  }
};
</script>

<template>
  <!-- Settings Modal -->
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="close"
    >
      <div class="absolute inset-0 bg-black/50" @click="close"></div>

      <!-- Bulletin board style modal - same as InviteManager -->
      <div
        class="relative bg-[#d4c4a8] rounded-lg w-full max-w-lg max-h-[80vh] shadow-2xl flex flex-col overflow-hidden"
        style="
          background-image: url(&quot;data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='corkNoise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.6' numOctaves='5' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23corkNoise)'/%3E%3C/svg%3E&quot;);
        "
      >
        <div class="absolute inset-0 bg-black/5 pointer-events-none"></div>

        <div class="p-6 border-b border-black/10 relative z-10 bg-[#c9b896]/80">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold text-gray-800">{{ $t('settings.title') }}</h2>
            <button
              @click="close"
              class="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-6 space-y-6 relative z-10">
          <div v-if="isLoading" class="text-center py-8">
            <p class="text-gray-600">{{ $t('common.loading') }}</p>
          </div>

          <div v-else class="space-y-6">
            <!-- Show Welcome Info Button - Only when inside a workshop -->
            <template v-if="workshop">
              <div
                class="paper-card cork-pin p-4 rounded-sm bg-(--color-postit-yellow)"
                :style="{ '--card-rotation': '-0.2deg' }"
              >
                <div class="paper-texture rounded-sm"></div>
                <div class="relative z-10">
                  <button
                    @click="$emit('showWelcome')"
                    class="cursor-pointer w-full px-4 py-3 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {{ $t('common.showWelcome') }}
                  </button>
                </div>
              </div>
            </template>

            <!-- Workshop Settings Section - Only when inside a workshop -->
            <template v-if="workshop">
              <div class="border-b border-black/10 pb-2">
                <h3 class="text-lg font-bold text-gray-800">
                  {{ $t('settings.workshopSettings') }}
                </h3>
              </div>

              <!-- Workshop Settings - Owner Only -->
              <template v-if="isWorkshopOwner">
                <!-- Lock Votes Setting - Pinned note -->
                <div
                  class="paper-card cork-pin p-5 rounded-sm bg-white"
                  :style="{ '--card-rotation': '-0.5deg' }"
                >
                  <div class="paper-texture rounded-sm"></div>

                  <div class="relative z-10 space-y-4">
                    <h3 class="text-gray-800 font-semibold">{{ $t('settings.lockVotes') }}</h3>
                    <p class="text-gray-600 text-sm">{{ $t('settings.lockVotesDescription') }}</p>

                    <div class="flex items-center justify-between py-2">
                      <span class="text-gray-700 font-medium">{{
                        votesLocked ? $t('common.locked') : $t('common.unlocked')
                      }}</span>
                      <div
                        @click="toggleVotesLocked"
                        class="physical-toggle"
                        :class="{ active: votesLocked }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Comments Enabled Setting - Pinned note -->
                <div
                  class="paper-card cork-pin p-5 rounded-sm bg-white"
                  :style="{ '--card-rotation': '0.3deg' }"
                >
                  <div class="paper-texture rounded-sm"></div>

                  <div class="relative z-10 space-y-4">
                    <h3 class="text-gray-800 font-semibold">
                      {{ $t('settings.commentsEnabled') }}
                    </h3>
                    <p class="text-gray-600 text-sm">{{ $t('settings.commentsDescription') }}</p>

                    <div class="flex items-center justify-between py-2">
                      <span class="text-gray-700 font-medium">{{
                        commentsEnabled ? $t('common.enabled') : $t('common.disabled')
                      }}</span>
                      <div
                        @click="toggleCommentsEnabled"
                        class="physical-toggle"
                        :class="{ active: commentsEnabled }"
                      ></div>
                    </div>
                  </div>
                </div>

                <!-- Delete Workshop Danger Zone -->
                <div
                  class="paper-card cork-pin p-4 rounded-sm bg-(--color-postit-pink)"
                  :style="{ '--card-rotation': '-0.3deg' }"
                >
                  <div class="paper-texture rounded-sm"></div>
                  <div class="relative z-10">
                    <h3 class="text-gray-800 font-semibold mb-2">
                      {{ $t('settings.workshopDangerZone') }}
                    </h3>
                    <p class="text-gray-700 text-sm mb-3">
                      Workshop: <strong>{{ workshop.name }}</strong>
                    </p>
                    <button
                      @click="showDeleteWorkshopConfirm = true"
                      class="push-button danger w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      {{ $t('confirm.deleteWorkshopButton') }}
                    </button>
                    <p class="text-gray-600 text-xs mt-2">
                      {{ $t('confirm.deleteWorkshop', { workshopName: workshop.name }) }}
                    </p>
                  </div>
                </div>
              </template>

              <!-- Viewer-only message for workshop settings -->
              <div
                v-else
                class="paper-card cork-pin p-4 rounded-sm bg-(--color-postit-yellow)"
                :style="{ '--card-rotation': '0.2deg' }"
              >
                <div class="paper-texture rounded-sm"></div>
                <div class="relative z-10">
                  <p class="text-gray-700 text-sm">
                    {{ $t('settings.workshopSettingsOwnerOnly') }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Personal Settings Section -->
            <div class="border-b border-black/10 pb-2">
              <h3 class="text-lg font-bold text-gray-800">{{ $t('settings.personalSettings') }}</h3>
            </div>

            <!-- Language Setting - Pinned note -->
            <div
              class="paper-card cork-pin p-5 rounded-sm bg-white"
              :style="{ '--card-rotation': '-0.5deg' }"
            >
              <div class="paper-texture rounded-sm"></div>

              <div class="relative z-10 space-y-4">
                <h3 class="text-gray-800 font-semibold">{{ $t('settings.language') }}</h3>
                <p class="text-gray-600 text-sm">{{ $t('settings.languageDescription') }}</p>

                <div class="flex items-center gap-4">
                  <SkeuoDropdown
                    v-model="selectedLocale"
                    :options="[
                      {
                        value: '',
                        label: $t('settings.browserDefault') + ' (' + browserLocaleName + ')',
                      },
                      ...SUPPORTED_LOCALES.map(loc => ({ value: loc, label: LOCALE_NAMES[loc] })),
                    ]"
                  />
                </div>
              </div>
            </div>

            <!-- Prefill Name Setting - Pinned note -->
            <div
              class="paper-card cork-pin p-5 rounded-sm bg-white"
              :style="{ '--card-rotation': '0.3deg' }"
            >
              <div class="paper-texture rounded-sm"></div>

              <div class="relative z-10 space-y-4">
                <h3 class="text-gray-800 font-semibold">{{ $t('settings.prefillName') }}</h3>
                <p class="text-gray-600 text-sm">{{ $t('settings.prefillNameDescription') }}</p>

                <div class="flex items-center justify-between py-2">
                  <span class="text-gray-700 font-medium">{{
                    prefillEnabled ? $t('common.enabled') : $t('common.disabled')
                  }}</span>
                  <div
                    @click="prefillEnabled = !prefillEnabled"
                    class="physical-toggle"
                    :class="{ active: prefillEnabled }"
                  ></div>
                </div>
              </div>
            </div>

            <hr class="border-black/10" />

            <!-- Logout - Pinned note -->
            <div
              class="paper-card cork-pin p-4 rounded-sm bg-(--color-postit-blue)"
              :style="{ '--card-rotation': '0.5deg' }"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10">
                <button
                  @click="handleLogout"
                  class="cursor-pointer w-full px-4 py-3 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  {{ $t('auth.signOut') }}
                </button>
              </div>
            </div>

            <!-- Account Danger Zone - Red warning paper -->
            <div
              class="paper-card cork-pin p-4 rounded-sm bg-(--color-postit-pink)"
              :style="{ '--card-rotation': '0.3deg' }"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10">
                <h3 class="text-gray-800 font-semibold mb-3">
                  {{ $t('settings.accountDangerZone') }}
                </h3>
                <button
                  @click="showDeleteAccountConfirm = true"
                  class="push-button danger w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  {{ $t('confirm.deleteAccountButton') }}
                </button>
                <p class="text-gray-600 text-xs mt-2">
                  {{ $t('confirm.deleteAccount') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Account Confirmation with Name Input -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDeleteAccountConfirm"
          class="fixed inset-0 z-100 flex items-center justify-center p-4"
          @click.self="showDeleteAccountConfirm = false"
        >
          <!-- Backdrop -->
          <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]"></div>

          <!-- Post-it style confirmation card -->
          <div
            class="relative bg-[#fecaca] rounded-sm shadow-2xl p-6 max-w-80 w-full text-center"
            style="transform: rotate(1deg)"
            @click.stop
          >
            <!-- Paper texture overlay -->
            <div
              class="absolute inset-0 rounded-sm opacity-40 pointer-events-none"
              style="
                background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');
                mix-blend-mode: multiply;
              "
            ></div>

            <!-- Tape strip -->
            <div
              class="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 bg-[rgba(255,255,255,0.5)] rounded-sm shadow-sm"
              style="transform: translateX(-50%) rotate(2deg)"
            ></div>

            <div class="relative z-10">
              <h3 class="text-lg font-bold text-gray-800 mb-2">
                {{ $t('confirm.deleteAccountTitle') }}
              </h3>
              <p class="text-sm text-gray-700 mb-4">
                {{ $t('confirm.deleteAccount') }}
              </p>

              <div class="mb-4 text-left">
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  {{
                    $t('confirm.typeToConfirm', { value: currentUser?.email || 'your@email.com' })
                  }}
                </label>
                <input
                  v-model="accountEmailConfirm"
                  type="text"
                  :placeholder="currentUser?.email || 'your@email.com'"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  @keyup.enter="canDeleteAccount && handleDeleteAccount()"
                />
              </div>

              <!-- Error message -->
              <div v-if="deleteError" class="mb-4 p-2 bg-red-100 rounded text-xs text-red-700">
                {{ deleteError }}
              </div>

              <div class="flex gap-3 justify-center">
                <button
                  @click.stop="
                    showDeleteAccountConfirm = false;
                    accountEmailConfirm = '';
                    deleteError = '';
                  "
                  :disabled="isDeleting"
                  class="px-4 py-2 text-sm bg-white/80 hover:bg-white text-gray-700 rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50"
                >
                  {{ $t('common.cancel') }}
                </button>
                <button
                  @click.stop="handleDeleteAccount"
                  :disabled="isDeleting || !canDeleteAccount"
                  :class="[
                    'px-4 py-2 text-sm rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50',
                    canDeleteAccount
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-400 text-white cursor-not-allowed',
                  ]"
                >
                  {{ isDeleting ? $t('common.loading') : $t('confirm.deleteAccountButton') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete Workshop Confirmation with Name Input -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showDeleteWorkshopConfirm"
          class="fixed inset-0 z-100 flex items-center justify-center p-4"
          @click.self="showDeleteWorkshopConfirm = false"
        >
          <!-- Backdrop -->
          <div class="absolute inset-0 bg-black/30 backdrop-blur-[2px]"></div>

          <!-- Post-it style confirmation card -->
          <div
            class="relative bg-[#fecaca] rounded-sm shadow-2xl p-6 max-w-80 w-full text-center"
            style="transform: rotate(-1deg)"
            @click.stop
          >
            <!-- Paper texture overlay -->
            <div
              class="absolute inset-0 rounded-sm opacity-40 pointer-events-none"
              style="
                background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noise%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%224%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noise)%22/%3E%3C/svg%3E');
                mix-blend-mode: multiply;
              "
            ></div>

            <!-- Tape strip -->
            <div
              class="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-5 bg-[rgba(255,255,255,0.5)] rounded-sm shadow-sm"
              style="transform: translateX(-50%) rotate(-2deg)"
            ></div>

            <div class="relative z-10">
              <h3 class="text-lg font-bold text-gray-800 mb-2">
                {{ $t('confirm.deleteWorkshopTitle') }}
              </h3>
              <p class="text-sm text-gray-700 mb-4">
                {{ $t('confirm.deleteWorkshop', { workshopName: workshop?.name }) }}
              </p>

              <div class="mb-4 text-left">
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  {{ $t('confirm.typeToConfirm', { value: workshop?.name }) }}
                </label>
                <input
                  v-model="workshopNameConfirm"
                  type="text"
                  :placeholder="workshop?.name"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  @keyup.enter="canDeleteWorkshop && handleDeleteWorkshop()"
                />
              </div>

              <!-- Error message -->
              <div v-if="deleteError" class="mb-4 p-2 bg-red-100 rounded text-xs text-red-700">
                {{ deleteError }}
              </div>

              <div class="flex gap-3 justify-center">
                <button
                  @click.stop="
                    showDeleteWorkshopConfirm = false;
                    workshopNameConfirm = '';
                    deleteError = '';
                  "
                  :disabled="isDeleting"
                  class="px-4 py-2 text-sm bg-white/80 hover:bg-white text-gray-700 rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50"
                >
                  {{ $t('common.cancel') }}
                </button>
                <button
                  @click.stop="handleDeleteWorkshop"
                  :disabled="isDeleting || !canDeleteWorkshop"
                  :class="[
                    'px-4 py-2 text-sm rounded-md transition-all duration-200 shadow-sm hover:shadow cursor-pointer disabled:opacity-50',
                    canDeleteWorkshop
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gray-400 text-white cursor-not-allowed',
                  ]"
                >
                  {{ isDeleting ? $t('common.loading') : $t('confirm.deleteWorkshopButton') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </Teleport>
</template>
